import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ClientSideTable from "../table/ClientSideTable";
import SimpleModal from "./SimpleModal";
import { useCallback, useMemo, useRef, useState } from "react";
import { Command, CommandStatus } from "../../types/command.types";
import { format } from "date-fns";
import OrderStatusBadge from "../OrderStatusBadge";
import { Flex } from "@chakra-ui/layout";
import TrashIcon from "../icons/TrashIcon";
import { useSimpleToast } from "../../hooks/toast.hooks";
import { fetchCommandByExposedId } from "../../queries/command.queries";
import { usePrepPurchaseOrder } from "../../hooks/purchase_order.hooks";
import { useCenters } from "../../hooks/centers.hooks";
import { printFileById } from "../../hooks/printers.hooks";

type PrepCommandModalProps = {
  isOpen: boolean;
  onClose: () => void;
  goBack: () => void;
  deliveryType: "UPS" | "OTHER" | null;
};

const PrepCommandModal = ({
  isOpen,
  onClose,
  goBack,
  deliveryType,
}: PrepCommandModalProps) => {
  const [commands, setCommands] = useState<Command[]>([]);
  const toast = useSimpleToast();
  const handleOnClose = () => {
    setCommands([]);
    onClose();
  }
  const handleRemoveItem = useCallback(
    (command: Command) => {
      setCommands(commands.filter((sc) => sc.id !== command.id));
    },
    [commands]
  );
  const commandCode = useRef("");
  const handleOnPaste = useCallback(
    async (event: React.ClipboardEvent) => {
      try {
        // 1. Parse code from clipboard
        const text = event.clipboardData.getData("text");
        // 2. Ensure not already in list
        if (
          commands.findIndex(
            (command) => command.exposedId.toString() === text
          ) !== -1
        ) {
          toast.info("Scan", "Le code est déjà présent dans la liste.");
          return;
        }
        // 3. Try to retrieve from backend
        const command = await fetchCommandByExposedId(text);
        if (command !== null) {
          setCommands([command, ...commands]);
        }
      } catch (err) {
        toast.error("Scan", "Le code n'a pas pu être lu.");
      }
    },
    [commands, toast]
  );
  const handleGoBack = useCallback(() => {
    setCommands([]);
    goBack();
  }, [setCommands, goBack]);
  const handleOnKeyDown = async (event: React.KeyboardEvent) => {
    switch (event.code) {
      case "Enter": {
        const code = commandCode.current.toLowerCase().split("§").join("-");
        commandCode.current = "";
        if (
          commands.findIndex(
            (command) => command.exposedId.toString() === code
          ) !== -1
        ) {
          toast.info("Scan", "Le code est déjà présent dans la liste.");
          return;
        }
        const command = await fetchCommandByExposedId(code);
        if (command === null) {
          toast.info("Scan", `Pas de commande trouvé pour le code '${code}'`);
          return;
        }
        if (command?.status !== CommandStatus.READY_FOR_DELIVERY) {
          toast.info("Scan", "Cette commande n'est pas prête à être livrée");
          return;
        }
        setCommands([command, ...commands]);
        break;
      }
      case "Shift":
      case "Meta":
      case "ShiftLeft":
        break;
      default: {
        commandCode.current = commandCode.current + event.key;
        break;
      }
    }
  };
  const columns = useMemo<ColumnDef<Command>[]>(
    () => [
      {
        accessorFn: (row) => row.exposedId,
        id: "exposed_id",
        header: "ID",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => {
          if (row.patient) {
            return `${row.patient.firstName} ${row.patient.lastName}`;
          }
          return `---`;
        },
        id: "patient",
        header: "Patient",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => format(row.createdAt, "dd/MM/yyyy"),
        id: "created_at",
        header: "Date de création",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => (
          <OrderStatusBadge
            halted={row.haltedAt != null}
            status={row.status}
          ></OrderStatusBadge>
        ),
        id: "status",
        header: "Statut",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => (
          <Flex justifyContent={"end"}>
            <TrashIcon
              color={"gray.500"}
              onClick={() => handleRemoveItem(row)}
            ></TrashIcon>
          </Flex>
        ),
        id: "more",
        header: "",
        cell: (info) => info.getValue(),
      },
    ],
    [handleRemoveItem]
  );
  const table = useReactTable({
    data: commands ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const { center } = useCenters();
  const prepPurchaseOrder = usePrepPurchaseOrder({
    onSuccess: async (data) => {
      if (deliveryType === "UPS" && data?.shippingLabelFileId) {
        printFileById(data.shippingLabelFileId, "Imprimante UPS");
      }
      printFileById(data.fileId, "Imprimante");
      handleOnClose();
      toast.success("Bon de livraison", "Le bon de livraison a bien été créé");
    },
    onError: () => {
      toast.error(
        "Bon de livraison",
        "Erreur lors de la création du bon de livraison"
      );
    },
  });
  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={handleOnClose}
      w="min(1800px, 80vw)"
      title="Préparation de commande"
      abortText="Retour"
      onAbort={handleGoBack}
      onPaste={handleOnPaste}
      onKeyDown={handleOnKeyDown}
      isPending={prepPurchaseOrder.isLoading}
      onClick={() => {
        if (center && commands.length) {
          prepPurchaseOrder.mutate({
            centerId: center.id,
            commands: commands.map((command) => command.id),
            deliveryType: deliveryType,
          });
        }
      }}
    >
      <ClientSideTable
        table={table}
        w={["auto", "240px", "200px", "300px", "10px"]}
        flexGrow={[1, 0, 0, 0, 0]}
      ></ClientSideTable>
    </SimpleModal>
  );
};

export default PrepCommandModal;
